import type { ZodType } from "zod";

import {
  AcceptCircleInviteResponseSchema,
  CircleInviteInfoResponseSchema,
  type CircleInviteInfo,
  CircleCommentsListResponseSchema,
  type CircleCommentResponse,
  type CreateCircleCommentInput,
  CreateCircleCommentResponseSchema,
  CircleInvitesListResponseSchema,
  CircleEventsListResponseSchema,
  CircleMembersListResponseSchema,
  CirclesListResponseSchema,
  type CircleEventResponse,
  type CircleInviteResponse,
  type InviteCircleMemberResult,
  CircleMemberGoalsResponseSchema,
  type CircleMemberGoalsResponse,
  type CircleMemberResponse,
  CircleProgressResponseSchema,
  type CircleProgressResponse,
  type CircleResponse,
  type CurrentUserResponse,
  CircleResponseSchema,
  CurrentUserResponseSchema,
  CreateCircleInviteResponseSchema,
  CreateCircleResponseSchema,
  type CreateCircleInput,
  type CreateDailyGoalInput,
  type DailyGoalHistoryResponse,
  DailyGoalHistoryResponseSchema,
  type DailyGoalResponse,
  DailyGoalCompletionMutationResponseSchema,
  DailyGoalMutationResponseSchema,
  DailyGoalSkipMutationResponseSchema,
  DailyGoalsListResponseSchema,
  DailyEntriesListResponseSchema,
  type DailyEntryResponse,
  type GoalCompletionKind,
  HealthResponseSchema,
  type HealthResponse,
  TodayDailyEntryResponseSchema,
  type TodayDailyEntryResponse,
  TodayDashboardResponseSchema,
  type TodayDashboardResponse,
  type UpsertDailyEntryInput,
  UpsertDailyEntryResponseSchema,
  type InviteCircleMemberInput,
  type NotificationPreferences,
  NotificationPreferencesResponseSchema,
  type PatchCurrentUserInput,
  type PatchDailyGoalInput,
  type RegisterPushTokenInput,
  type UpdateCircleInput,
  type UpdateNotificationPreferencesInput,
} from "@consistency-circle/shared";

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly fetchImplementation?: typeof fetch;
  readonly headers?: HeadersInit;
  readonly getHeaders?: () => HeadersInit | Promise<HeadersInit>;
}

export interface ApiClient {
  getHealth(): Promise<HealthResponse>;
  getCurrentUser(): Promise<CurrentUserResponse["user"]>;
  patchCurrentUser(input: PatchCurrentUserInput): Promise<CurrentUserResponse["user"]>;
  getTodayEntry(): Promise<TodayDailyEntryResponse>;
  getDailyEntries(from: string, to: string): Promise<DailyEntryResponse[]>;
  upsertDailyEntry(date: string, input: UpsertDailyEntryInput): Promise<DailyEntryResponse>;
  getTodayDashboard(): Promise<TodayDashboardResponse>;
  getDashboardForDate(date: string): Promise<TodayDashboardResponse>;
  getDailyGoals(): Promise<DailyGoalResponse[]>;
  createDailyGoal(input: CreateDailyGoalInput): Promise<DailyGoalResponse>;
  patchDailyGoal(goalId: string, input: PatchDailyGoalInput): Promise<DailyGoalResponse>;
  completeDailyGoalSlotToday(
    goalId: string,
    slotId: string,
    kind?: GoalCompletionKind,
  ): Promise<void>;
  undoDailyGoalSlotToday(goalId: string, slotId: string): Promise<void>;
  skipDailyGoalSlotToday(goalId: string, slotId: string): Promise<void>;
  unskipDailyGoalSlotToday(goalId: string, slotId: string): Promise<void>;
  getDailyGoalHistory(from: string, to: string): Promise<DailyGoalHistoryResponse>;
  createCircle(input: CreateCircleInput): Promise<CircleResponse>;
  getCircles(): Promise<CircleResponse[]>;
  getCircle(circleId: string): Promise<CircleResponse>;
  updateCircle(circleId: string, input: UpdateCircleInput): Promise<CircleResponse>;
  leaveCircle(circleId: string): Promise<void>;
  removeCircleMember(circleId: string, memberId: string): Promise<void>;
  getCircleMembers(circleId: string): Promise<CircleMemberResponse[]>;
  getCircleInvites(circleId: string): Promise<CircleInviteResponse[]>;
  getCircleEvents(circleId: string): Promise<CircleEventResponse[]>;
  getCircleComments(circleId: string): Promise<CircleCommentResponse[]>;
  createCircleComment(
    circleId: string,
    input: CreateCircleCommentInput,
  ): Promise<CircleCommentResponse>;
  getCircleProgress(circleId: string): Promise<CircleProgressResponse>;
  getCircleMemberGoals(circleId: string, userId: string): Promise<CircleMemberGoalsResponse>;
  inviteCircleMember(circleId: string, input: InviteCircleMemberInput): Promise<InviteCircleMemberResult>;
  revokeCircleInvite(circleId: string, inviteId: string): Promise<void>;
  getCircleInviteInfo(token: string): Promise<CircleInviteInfo>;
  acceptCircleInvite(token: string): Promise<CircleResponse>;
  getNotificationPreferences(): Promise<NotificationPreferences>;
  updateNotificationPreferences(
    input: UpdateNotificationPreferencesInput,
  ): Promise<NotificationPreferences>;
  registerPushToken(input: RegisterPushTokenInput): Promise<void>;
  removePushToken(token: string): Promise<void>;
}

interface RequestOptions<TResponse> {
  readonly path: string;
  readonly schema: ZodType<TResponse>;
  readonly init?: RequestInit;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const request = async <TResponse>({
    path,
    schema,
    init,
  }: RequestOptions<TResponse>): Promise<TResponse> => {
    const fetchImplementation = options.fetchImplementation ?? fetch;
    const dynamicHeaders = options.getHeaders ? await options.getHeaders() : undefined;
    const response = await fetchImplementation(`${stripTrailingSlash(options.baseUrl)}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        "content-type": "application/json",
        ...options.headers,
        ...dynamicHeaders,
        ...init?.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      const message = errorText.trim();
      throw new Error(
        message.length > 0 ? `Request failed with status ${response.status}: ${message}` : `Request failed with status ${response.status}`,
      );
    }

    const data: unknown = await response.json();
    return schema.parse(data);
  };

  const requestNoContent = async (path: string, init?: RequestInit): Promise<void> => {
    const fetchImplementation = options.fetchImplementation ?? fetch;
    const dynamicHeaders = options.getHeaders ? await options.getHeaders() : undefined;
    const response = await fetchImplementation(`${stripTrailingSlash(options.baseUrl)}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        "content-type": "application/json",
        ...options.headers,
        ...dynamicHeaders,
        ...init?.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      const message = errorText.trim();
      throw new Error(
        message.length > 0 ? `Request failed with status ${response.status}: ${message}` : `Request failed with status ${response.status}`,
      );
    }
  };

  return {
    getHealth(): Promise<HealthResponse> {
      return request({
        path: "/health",
        schema: HealthResponseSchema,
      });
    },
    async getCurrentUser(): Promise<CurrentUserResponse["user"]> {
      const response = await request({
        path: "/me",
        schema: CurrentUserResponseSchema,
      });

      return response.user;
    },
    async patchCurrentUser(input: PatchCurrentUserInput): Promise<CurrentUserResponse["user"]> {
      const response = await request({
        path: "/me",
        schema: CurrentUserResponseSchema,
        init: {
          method: "PATCH",
          body: JSON.stringify(input),
        },
      });

      return response.user;
    },
    getTodayEntry(): Promise<TodayDailyEntryResponse> {
      return request({
        path: "/daily-entries/today",
        schema: TodayDailyEntryResponseSchema,
      });
    },
    async getDailyEntries(from: string, to: string): Promise<DailyEntryResponse[]> {
      const response = await request({
        path: `/daily-entries?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        schema: DailyEntriesListResponseSchema,
      });

      return response.entries;
    },
    async upsertDailyEntry(
      date: string,
      input: UpsertDailyEntryInput,
    ): Promise<DailyEntryResponse> {
      const response = await request({
        path: `/daily-entries/${encodeURIComponent(date)}`,
        schema: UpsertDailyEntryResponseSchema,
        init: {
          method: "PUT",
          body: JSON.stringify(input),
        },
      });

      return response.entry;
    },
    getTodayDashboard(): Promise<TodayDashboardResponse> {
      return request({
        path: "/dashboard/today",
        schema: TodayDashboardResponseSchema,
      });
    },
    getDashboardForDate(date: string): Promise<TodayDashboardResponse> {
      return request({
        path: `/dashboard/${encodeURIComponent(date)}`,
        schema: TodayDashboardResponseSchema,
      });
    },
    async getDailyGoals(): Promise<DailyGoalResponse[]> {
      const response = await request({
        path: "/daily-goals",
        schema: DailyGoalsListResponseSchema,
      });

      return response.goals;
    },
    async createDailyGoal(input: CreateDailyGoalInput): Promise<DailyGoalResponse> {
      const response = await request({
        path: "/daily-goals",
        schema: DailyGoalMutationResponseSchema,
        init: {
          method: "POST",
          body: JSON.stringify(input),
        },
      });

      return response.goal;
    },
    async patchDailyGoal(
      goalId: string,
      input: PatchDailyGoalInput,
    ): Promise<DailyGoalResponse> {
      const response = await request({
        path: `/daily-goals/${encodeURIComponent(goalId)}`,
        schema: DailyGoalMutationResponseSchema,
        init: {
          method: "PATCH",
          body: JSON.stringify(input),
        },
      });

      return response.goal;
    },
    async completeDailyGoalSlotToday(
      goalId: string,
      slotId: string,
      kind?: GoalCompletionKind,
    ): Promise<void> {
      await request({
        path: `/daily-goals/${encodeURIComponent(goalId)}/slots/${encodeURIComponent(slotId)}/completions/today`,
        schema: DailyGoalCompletionMutationResponseSchema,
        init: {
          method: "POST",
          body: JSON.stringify(kind ? { kind } : {}),
        },
      });
    },
    async undoDailyGoalSlotToday(goalId: string, slotId: string): Promise<void> {
      await request({
        path: `/daily-goals/${encodeURIComponent(goalId)}/slots/${encodeURIComponent(slotId)}/completions/today`,
        schema: DailyGoalCompletionMutationResponseSchema,
        init: {
          method: "DELETE",
        },
      });
    },
    async skipDailyGoalSlotToday(goalId: string, slotId: string): Promise<void> {
      await request({
        path: `/daily-goals/${encodeURIComponent(goalId)}/slots/${encodeURIComponent(slotId)}/skips/today`,
        schema: DailyGoalSkipMutationResponseSchema,
        init: {
          method: "POST",
        },
      });
    },
    async unskipDailyGoalSlotToday(goalId: string, slotId: string): Promise<void> {
      await request({
        path: `/daily-goals/${encodeURIComponent(goalId)}/slots/${encodeURIComponent(slotId)}/skips/today`,
        schema: DailyGoalSkipMutationResponseSchema,
        init: {
          method: "DELETE",
        },
      });
    },
    getDailyGoalHistory(from: string, to: string): Promise<DailyGoalHistoryResponse> {
      return request({
        path: `/daily-goals/history?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        schema: DailyGoalHistoryResponseSchema,
      });
    },
    async createCircle(input: CreateCircleInput): Promise<CircleResponse> {
      const response = await request({
        path: "/circles",
        schema: CreateCircleResponseSchema,
        init: {
          method: "POST",
          body: JSON.stringify(input),
        },
      });

      return response.circle;
    },
    async getCircles(): Promise<CircleResponse[]> {
      const response = await request({
        path: "/circles",
        schema: CirclesListResponseSchema,
      });

      return response.circles;
    },
    getCircle(circleId: string): Promise<CircleResponse> {
      return request({
        path: `/circles/${encodeURIComponent(circleId)}`,
        schema: CircleResponseSchema,
      });
    },
    updateCircle(circleId: string, input: UpdateCircleInput): Promise<CircleResponse> {
      return request({
        path: `/circles/${encodeURIComponent(circleId)}`,
        schema: CircleResponseSchema,
        init: {
          method: "PATCH",
          body: JSON.stringify(input),
        },
      });
    },
    leaveCircle(circleId: string): Promise<void> {
      return requestNoContent(`/circles/${encodeURIComponent(circleId)}/leave`, {
        method: "POST",
      });
    },
    removeCircleMember(circleId: string, memberId: string): Promise<void> {
      return requestNoContent(
        `/circles/${encodeURIComponent(circleId)}/members/${encodeURIComponent(memberId)}`,
        { method: "DELETE" },
      );
    },
    async getCircleMembers(circleId: string): Promise<CircleMemberResponse[]> {
      const response = await request({
        path: `/circles/${encodeURIComponent(circleId)}/members`,
        schema: CircleMembersListResponseSchema,
      });

      return response.members;
    },
    async getCircleInvites(circleId: string): Promise<CircleInviteResponse[]> {
      const response = await request({
        path: `/circles/${encodeURIComponent(circleId)}/invites`,
        schema: CircleInvitesListResponseSchema,
      });

      return response.invites;
    },
    async getCircleEvents(circleId: string): Promise<CircleEventResponse[]> {
      const response = await request({
        path: `/circles/${encodeURIComponent(circleId)}/events`,
        schema: CircleEventsListResponseSchema,
      });

      return response.events;
    },
    async getCircleComments(circleId: string): Promise<CircleCommentResponse[]> {
      const response = await request({
        path: `/circles/${encodeURIComponent(circleId)}/comments`,
        schema: CircleCommentsListResponseSchema,
      });

      return response.comments;
    },
    async createCircleComment(
      circleId: string,
      input: CreateCircleCommentInput,
    ): Promise<CircleCommentResponse> {
      const response = await request({
        path: `/circles/${encodeURIComponent(circleId)}/comments`,
        schema: CreateCircleCommentResponseSchema,
        init: {
          method: "POST",
          body: JSON.stringify(input),
        },
      });

      return response.comment;
    },
    getCircleProgress(circleId: string): Promise<CircleProgressResponse> {
      return request({
        path: `/circles/${encodeURIComponent(circleId)}/progress`,
        schema: CircleProgressResponseSchema,
      });
    },
    getCircleMemberGoals(circleId: string, userId: string): Promise<CircleMemberGoalsResponse> {
      return request({
        path: `/circles/${encodeURIComponent(circleId)}/members/${encodeURIComponent(userId)}/goals`,
        schema: CircleMemberGoalsResponseSchema,
      });
    },
    async inviteCircleMember(
      circleId: string,
      input: InviteCircleMemberInput,
    ): Promise<InviteCircleMemberResult> {
      const response = await request({
        path: `/circles/${encodeURIComponent(circleId)}/invites`,
        schema: CreateCircleInviteResponseSchema,
        init: {
          method: "POST",
          body: JSON.stringify(input),
        },
      });

      return response.result;
    },
    async revokeCircleInvite(circleId: string, inviteId: string): Promise<void> {
      const fetchImplementation = options.fetchImplementation ?? fetch;
      const dynamicHeaders = options.getHeaders ? await options.getHeaders() : undefined;
      await fetchImplementation(
        `${stripTrailingSlash(options.baseUrl)}/circles/${encodeURIComponent(circleId)}/invites/${encodeURIComponent(inviteId)}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            ...options.headers,
            ...dynamicHeaders,
          },
        },
      );
    },
    async getCircleInviteInfo(token: string): Promise<CircleInviteInfo> {
      const response = await request({
        path: `/circle-invites/${encodeURIComponent(token)}`,
        schema: CircleInviteInfoResponseSchema,
      });

      return response.invite;
    },
    async acceptCircleInvite(token: string): Promise<CircleResponse> {
      const response = await request({
        path: `/circle-invites/${encodeURIComponent(token)}/accept`,
        schema: AcceptCircleInviteResponseSchema,
        init: {
          method: "POST",
        },
      });

      return response.circle;
    },
    async getNotificationPreferences(): Promise<NotificationPreferences> {
      const response = await request({
        path: "/me/notification-preferences",
        schema: NotificationPreferencesResponseSchema,
      });

      return response.preferences;
    },
    async updateNotificationPreferences(
      input: UpdateNotificationPreferencesInput,
    ): Promise<NotificationPreferences> {
      const response = await request({
        path: "/me/notification-preferences",
        schema: NotificationPreferencesResponseSchema,
        init: {
          method: "PATCH",
          body: JSON.stringify(input),
        },
      });

      return response.preferences;
    },
    registerPushToken(input: RegisterPushTokenInput): Promise<void> {
      return requestNoContent("/me/push-tokens", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    removePushToken(token: string): Promise<void> {
      return requestNoContent(`/me/push-tokens/${encodeURIComponent(token)}`, {
        method: "DELETE",
      });
    },
  };
}

function stripTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}
